import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import AutoGrowTextarea from '../../components/AutoGrowTextarea/AutoGrowTextarea';
import PageMeta from '../../components/SEO/PageMeta';
import {
  getStringToolPath,
  getStringToolRoute,
  getStringToolsByCategory,
  getStringToolStructuredData,
  LEGACY_STRING_TOOL_SLUGS,
  STRING_TOOL_CATEGORY_LABELS,
  StringToolCategory,
  StringToolSlug,
} from '../../config/stringTools';
import {
  buildDiffRows,
  formatDiffChar,
  getTextStats,
  removeSpaces,
  reverseText,
  toCamelCase,
  toKebabCase,
  toLowercase,
  toPascalCase,
  toSentenceCase,
  toSnakeCase,
  toTitleCase,
  toUppercase,
  trimWhitespace,
} from '../../utils/stringTransforms';
import styles from './StringToolsPage.module.css';

const SAMPLE_TEXT = 'SaveItPro string tools make text cleanup fast';

const TOOL_CATEGORIES: StringToolCategory[] = ['case', 'analyze', 'transform', 'compare'];

export default function StringToolsPage() {
  const { toolSlug } = useParams<{ toolSlug: string }>();
  const route = getStringToolRoute(toolSlug);
  const isMasterPage = !toolSlug;
  const [input, setInput] = useState(SAMPLE_TEXT);
  const [compareInput, setCompareInput] = useState('saveitpro string tools make text cleanup fast');
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [isCaseSensitive, setIsCaseSensitive] = useState(true);

  const results = useMemo(
    () => ({
      uppercase: toUppercase(input),
      lowercase: toLowercase(input),
      titleCase: toTitleCase(input),
      sentenceCase: toSentenceCase(input),
      camelCase: toCamelCase(input),
      pascalCase: toPascalCase(input),
      snakeCase: toSnakeCase(input),
      kebabCase: toKebabCase(input),
      reverseText: reverseText(input),
      trimWhitespace: trimWhitespace(input),
      removeSpaces: removeSpaces(input),
      stats: getTextStats(input),
    }),
    [input],
  );

  const diffRows = useMemo(
    () => buildDiffRows(input, compareInput, isCaseSensitive),
    [compareInput, input, isCaseSensitive],
  );
  const unmatchedCount = diffRows.filter((row) => !row.matches).length;

  useEffect(() => {
    if (!route || isMasterPage) {
      return;
    }

    const targetId = route.slug === 'text-compare' ? 'text-compare' : route.slug;
    const element = document.getElementById(targetId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isMasterPage, route]);

  if (toolSlug && LEGACY_STRING_TOOL_SLUGS[toolSlug]) {
    return <Navigate to={getStringToolPath(LEGACY_STRING_TOOL_SLUGS[toolSlug])} replace />;
  }

  if (toolSlug && !route) {
    return <Navigate to={getStringToolPath('uppercase')} replace />;
  }

  const pageTitle = route?.title ?? 'Free Online String Tools';
  const pageDescription =
    route?.description ??
    'Free online string tools for case conversion, character counting, whitespace cleanup, text reversing, and character-by-character text comparison.';
  const path = route ? getStringToolPath(route.slug) : '/string-tools';
  const activeSlug = route?.slug;

  async function handleCopy(slug: string, value: string) {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopiedSlug(slug);
      window.setTimeout(() => setCopiedSlug(null), 1600);
    } catch {
      setCopiedSlug(null);
    }
  }

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        path={path}
        structuredData={route ? getStringToolStructuredData(route) : undefined}
      />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden> / </span>
        {route ? (
          <>
            <Link to="/string-tools">String Tools</Link>
            <span aria-hidden> / </span>
            <span>{route.shortLabel}</span>
          </>
        ) : (
          <span>String Tools</span>
        )}
      </nav>

      <section className={styles.hero}>
        <p className={styles.eyebrow}>Free online string tools</p>
        <h1>{pageTitle}</h1>
        <p>{pageDescription}</p>
      </section>

      <nav className={styles.toolNavGroups} aria-label="String tool shortcuts">
        {TOOL_CATEGORIES.map((category) => (
          <div key={category} className={styles.toolNavGroup}>
            <span className={styles.toolNavLabel}>{STRING_TOOL_CATEGORY_LABELS[category]}</span>
            <div className={styles.toolNav}>
              {getStringToolsByCategory(category).map((tool) => (
                <Link
                  key={tool.slug}
                  to={getStringToolPath(tool.slug)}
                  className={`${styles.toolLink} ${
                    tool.slug === activeSlug ? styles.activeToolLink : ''
                  }`}
                >
                  {tool.shortLabel}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {(isMasterPage || activeSlug !== 'text-compare') && (
        <section
          className={`${styles.workspace} ${
            isMasterPage ? styles.masterWorkspace : styles.dedicatedWorkspace
          }`}
          aria-labelledby="input-heading"
        >
          <div className={styles.inputPanel}>
            <label htmlFor="string-input">
              <span id="input-heading">Enter your text</span>
              <AutoGrowTextarea
                id="string-input"
                value={input}
                onValueChange={setInput}
                autoGrow={isMasterPage}
                rows={isMasterPage ? 7 : 14}
                placeholder="Type or paste text here..."
              />
            </label>
            <p className={styles.helperText}>
              {isMasterPage
                ? 'One input powers every converter below. Results update live as you type.'
                : 'This input powers the selected string tool. Results update live as you type.'}
            </p>
          </div>

          <div className={styles.resultSections}>
            {(isMasterPage || isCaseSlug(activeSlug)) && (
              <ResultSection
                title={isMasterPage ? 'Case converters' : (route?.label ?? 'Case converter')}
              >
                {(isMasterPage || activeSlug === 'uppercase') && (
                  <ResultCard
                    slug="uppercase"
                    activeSlug={activeSlug}
                    title="Uppercase"
                    value={results.uppercase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'lowercase') && (
                  <ResultCard
                    slug="lowercase"
                    activeSlug={activeSlug}
                    title="Lowercase"
                    value={results.lowercase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'title-case') && (
                  <ResultCard
                    slug="title-case"
                    activeSlug={activeSlug}
                    title="Title case"
                    value={results.titleCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'sentence-case') && (
                  <ResultCard
                    slug="sentence-case"
                    activeSlug={activeSlug}
                    title="Sentence case"
                    value={results.sentenceCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'camel-case') && (
                  <ResultCard
                    slug="camel-case"
                    activeSlug={activeSlug}
                    title="Camel case"
                    value={results.camelCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'pascal-case') && (
                  <ResultCard
                    slug="pascal-case"
                    activeSlug={activeSlug}
                    title="Pascal case"
                    value={results.pascalCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'snake-case') && (
                  <ResultCard
                    slug="snake-case"
                    activeSlug={activeSlug}
                    title="Snake case"
                    value={results.snakeCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'kebab-case') && (
                  <ResultCard
                    slug="kebab-case"
                    activeSlug={activeSlug}
                    title="Kebab case"
                    value={results.kebabCase}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}

            {(isMasterPage || activeSlug === 'character-counter') && (
              <ResultSection title={isMasterPage ? 'Counters' : 'Character & word counter'}>
                <article
                  id="character-counter"
                  className={`${styles.resultCard} ${styles.statsCard} ${
                    activeSlug === 'character-counter' ? styles.activeResultCard : ''
                  }`}
                >
                  <div className={styles.cardHeader}>
                    <h2>Character & word counter</h2>
                  </div>
                  <dl className={styles.statsGrid}>
                    <div>
                      <dt>Characters</dt>
                      <dd>{results.stats.characters}</dd>
                    </div>
                    <div>
                      <dt>Without spaces</dt>
                      <dd>{results.stats.charactersNoSpaces}</dd>
                    </div>
                    <div>
                      <dt>Words</dt>
                      <dd>{results.stats.words}</dd>
                    </div>
                    <div>
                      <dt>Lines</dt>
                      <dd>{results.stats.lines}</dd>
                    </div>
                  </dl>
                </article>
              </ResultSection>
            )}

            {(isMasterPage || isCleanupSlug(activeSlug)) && (
              <ResultSection
                title={isMasterPage ? 'Text cleanup' : (route?.label ?? 'Text cleanup')}
              >
                {(isMasterPage || activeSlug === 'reverse-text') && (
                  <ResultCard
                    slug="reverse-text"
                    activeSlug={activeSlug}
                    title="Reverse text"
                    value={results.reverseText}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'trim-whitespace') && (
                  <ResultCard
                    slug="trim-whitespace"
                    activeSlug={activeSlug}
                    title="Trim whitespace"
                    value={results.trimWhitespace}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
                {(isMasterPage || activeSlug === 'remove-spaces') && (
                  <ResultCard
                    slug="remove-spaces"
                    activeSlug={activeSlug}
                    title="Remove spaces"
                    value={results.removeSpaces}
                    copiedSlug={copiedSlug}
                    onCopy={handleCopy}
                  />
                )}
              </ResultSection>
            )}
          </div>
        </section>
      )}

      {(isMasterPage || activeSlug === 'text-compare') && (
        <section
          id="text-compare"
          className={`${styles.diffSection} ${
            activeSlug === 'text-compare' ? styles.activeResultCard : ''
          }`}
          aria-labelledby="diff-heading"
        >
          <div className={styles.diffHeader}>
            <div>
              <p className={styles.eyebrow}>Position-based comparison</p>
              <h2 id="diff-heading">Text compare</h2>
              <p>
                Characters are compared at the same index. Changed, missing, or extra characters are
                highlighted in red with a total mismatch count.
              </p>
            </div>
            <div className={styles.unmatchedCount}>
              <span>{unmatchedCount}</span>
              unmatched
            </div>
          </div>

          <div className={styles.diffInputs}>
            <label htmlFor="string-diff-left">
              Original text
              <AutoGrowTextarea
                id="string-diff-left"
                value={input}
                onValueChange={setInput}
                autoGrow={isMasterPage}
                rows={isMasterPage ? 4 : 10}
                placeholder="Original text"
              />
            </label>
            <label htmlFor="string-diff-right">
              Compare with
              <AutoGrowTextarea
                id="string-diff-right"
                value={compareInput}
                onValueChange={setCompareInput}
                autoGrow={isMasterPage}
                rows={isMasterPage ? 4 : 10}
                placeholder="Compare with..."
              />
            </label>
          </div>

          <fieldset className={styles.compareOptions}>
            <legend>Comparison mode</legend>
            <label>
              <input
                type="radio"
                name="comparison-mode"
                checked={isCaseSensitive}
                onChange={() => setIsCaseSensitive(true)}
              />
              Case sensitive
            </label>
            <label>
              <input
                type="radio"
                name="comparison-mode"
                checked={!isCaseSensitive}
                onChange={() => setIsCaseSensitive(false)}
              />
              Case insensitive
            </label>
          </fieldset>

          <div className={styles.diffLegend} aria-label="Diff legend">
            <span>
              <i className={styles.matchSwatch} aria-hidden /> matched
            </span>
            <span>
              <i className={styles.mismatchSwatch} aria-hidden /> unmatched
            </span>
          </div>

          {diffRows.length > 0 ? (
            <>
              <DiffAlignedView rows={diffRows} />
              <DiffIndexedView rows={diffRows} />
            </>
          ) : (
            <p className={styles.emptyDiff}>Enter two strings to compare characters by position.</p>
          )}
        </section>
      )}
    </>
  );
}

function DiffAlignedView({
  rows,
}: {
  rows: Array<{ index: number; left: string | null; right: string | null; matches: boolean }>;
}) {
  return (
    <div className={styles.diffAligned} aria-label="Aligned full string comparison">
      <DiffAlignedTextRow label="Original" side="left" rows={rows} />
      <DiffAlignedTextRow label="Compare" side="right" rows={rows} />
    </div>
  );
}

function DiffIndexedView({
  rows,
}: {
  rows: Array<{ index: number; left: string | null; right: string | null; matches: boolean }>;
}) {
  return (
    <div className={styles.diffIndexed} aria-label="Indexed character comparison">
      <h3>Indexed comparison</h3>
      <div className={styles.diffIndexedScroller}>
        <div className={styles.diffIndexedGrid}>
          {rows.map((row) => (
            <div key={row.index} className={styles.diffIndexedPair}>
              <span className={styles.diffIndex}>{row.index + 1}</span>
              <span className={row.matches ? styles.matchChar : styles.mismatchChar}>
                {formatDiffChar(row.left)}
              </span>
              <span className={row.matches ? styles.matchChar : styles.mismatchChar}>
                {formatDiffChar(row.right)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DiffAlignedTextRow({
  label,
  side,
  rows,
}: {
  label: string;
  side: 'left' | 'right';
  rows: Array<{ index: number; left: string | null; right: string | null; matches: boolean }>;
}) {
  return (
    <div className={styles.diffAlignedRow}>
      <span className={styles.diffAlignedLabel}>{label}</span>
      <div className={styles.diffAlignedCells}>
        {rows.map((row) => (
          <span
            key={row.index}
            className={`${styles.diffTextCell} ${
              row.matches ? styles.diffTextMatchCell : styles.diffTextMismatchCell
            }`}
          >
            {formatInlineDiffChar(side === 'left' ? row.left : row.right)}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatInlineDiffChar(value: string | null): string {
  if (value === null) {
    return '∅';
  }

  if (value === ' ') {
    return '\u00A0';
  }

  if (value === '\n') {
    return '↵\n';
  }

  if (value === '\t') {
    return '\u00A0\u00A0';
  }

  return value;
}

function ResultSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.resultSection}>
      <h2 className={styles.resultSectionTitle}>{title}</h2>
      <div className={styles.resultGrid}>{children}</div>
    </section>
  );
}

function ResultCard({
  slug,
  activeSlug,
  title,
  value,
  copiedSlug,
  onCopy,
}: {
  slug: StringToolSlug;
  activeSlug: StringToolSlug | undefined;
  title: string;
  value: string;
  copiedSlug: string | null;
  onCopy: (slug: string, value: string) => void;
}) {
  const isCopied = copiedSlug === slug;

  return (
    <article
      id={slug}
      className={`${styles.resultCard} ${slug === activeSlug ? styles.activeResultCard : ''}`}
    >
      <div className={styles.cardHeader}>
        <h3>{title}</h3>
        <button
          type="button"
          className={styles.copyButton}
          onClick={() => onCopy(slug, value)}
          disabled={!value}
        >
          {isCopied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <output>{value || '—'}</output>
    </article>
  );
}

function isCaseSlug(slug: StringToolSlug | undefined): boolean {
  return (
    slug === 'uppercase' ||
    slug === 'lowercase' ||
    slug === 'title-case' ||
    slug === 'sentence-case' ||
    slug === 'camel-case' ||
    slug === 'pascal-case' ||
    slug === 'snake-case' ||
    slug === 'kebab-case'
  );
}

function isCleanupSlug(slug: StringToolSlug | undefined): boolean {
  return slug === 'reverse-text' || slug === 'trim-whitespace' || slug === 'remove-spaces';
}
